import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const initialForm = {
  student_id: "",
  relationship: "GUARDIAN",
  is_primary_contact: false,
  can_receive_results: true,
  can_receive_finance_alerts: true,
  can_collect_student: false,
};

function ParentLinkStudentModal({ parent, onClose, onUpdated }) {
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!parent) return;

    const loadStudents = async () => {
      try {
        const response = await axiosInstance.get("parents/linkable-students/");
        setStudents(response.data || []);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load students.");
      }
    };

    loadStudents();
  }, [parent]);

  const filteredStudents = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return students;
    return students.filter((student) => {
      return (
        student.full_name?.toLowerCase().includes(term) ||
        student.student_number?.toLowerCase().includes(term) ||
        student.current_class?.toLowerCase().includes(term)
      );
    });
  }, [students, searchTerm]);

  if (!parent) return null;

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.student_id) {
      toast.warning("Please select a student to link.");
      return;
    }

    try {
      setSaving(true);
      const response = await axiosInstance.post(
        `parents/${parent.id}/link-student/`,
        formData
      );
      toast.success("Student linked successfully.");
      setFormData(initialForm);
      onUpdated(response.data.parent);
    } catch (error) {
      console.error(error);
      const data = error.response?.data;
      const message =
        data?.detail ||
        Object.values(data || {})?.flat?.()?.[0] ||
        "Failed to link student.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const removeLink = async (linkId) => {
    if (!window.confirm("Remove this student link?")) return;

    try {
      const response = await axiosInstance.delete(`parents/student-links/${linkId}/`);
      toast.success("Student link removed.");
      onUpdated(response.data.parent);
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove student link.");
    }
  };

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,0.45)" }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow">
          <div className="modal-header">
            <div>
              <h5 className="modal-title fw-bold">Link Student</h5>
              <small className="text-muted">{parent.full_name}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <div className="row g-4">
              <div className="col-lg-7">
                <div className="card border-0 bg-light">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="fw-bold mb-0">Choose Student</h6>
                      <input className="form-control" style={{ maxWidth: 280 }} placeholder="Search student..." value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} />
                    </div>
                    <div style={{ maxHeight: 360, overflowY: "auto" }}>
                      {filteredStudents.map((student) => (
                        <label key={student.id} className="border rounded-3 p-3 d-flex align-items-center gap-3 mb-2 bg-white">
                          <input type="radio" name="student_id" value={student.id} checked={String(formData.student_id) === String(student.id)} onChange={handleChange} />
                          <span>
                            <span className="d-block fw-bold">{student.full_name}</span>
                            <small className="text-muted">{student.student_number} · {student.current_class || "No class"}</small>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-5">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Relationship</label>
                    <select name="relationship" className="form-select" value={formData.relationship} onChange={handleChange}>
                      <option value="FATHER">Father</option>
                      <option value="MOTHER">Mother</option>
                      <option value="GUARDIAN">Guardian</option>
                      <option value="SPONSOR">Sponsor</option>
                      <option value="OTHER">Other</option>
                    </select>
                  </div>
                  <label className="form-check mb-2"><input type="checkbox" name="is_primary_contact" className="form-check-input" checked={formData.is_primary_contact} onChange={handleChange} /><span className="form-check-label">Primary contact</span></label>
                  <label className="form-check mb-2"><input type="checkbox" name="can_receive_results" className="form-check-input" checked={formData.can_receive_results} onChange={handleChange} /><span className="form-check-label">Can receive results</span></label>
                  <label className="form-check mb-2"><input type="checkbox" name="can_receive_finance_alerts" className="form-check-input" checked={formData.can_receive_finance_alerts} onChange={handleChange} /><span className="form-check-label">Can receive finance alerts</span></label>
                  <label className="form-check mb-4"><input type="checkbox" name="can_collect_student" className="form-check-input" checked={formData.can_collect_student} onChange={handleChange} /><span className="form-check-label">Can collect student</span></label>

                  <button className="btn btn-success w-100" disabled={saving}>
                    {saving ? "Linking..." : "Link Student"}
                  </button>
                </form>

                <hr />
                <h6 className="fw-bold">Currently Linked</h6>
                {parent.linked_students?.length ? (
                  parent.linked_students.map((link) => (
                    <div key={link.link_id} className="border rounded-3 p-2 d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <div className="fw-semibold">{link.student_name}</div>
                        <small className="text-muted">{link.relationship}</small>
                      </div>
                      <button className="btn btn-sm btn-light border" title="Remove link" onClick={() => removeLink(link.link_id)}>
                        <i className="bi bi-x-lg text-danger"></i>
                      </button>
                    </div>
                  ))
                ) : (
                  <small className="text-muted">No linked students yet.</small>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-light" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ParentLinkStudentModal;
